import type { InSnipCard } from "./types";

/**
 * Candidate lines a reader can turn into a shareable quote card: the essence (the
 * one-line insight), the standout body sentences, then the why-it-matters line. Each is
 * whitespace-normalised, deduped, and kept to a readable length so it stands alone as a
 * good pull-quote. Pure and side-effect free, so it is unit-tested directly.
 */
export function quoteCandidates(card: InSnipCard, max = 6): string[] {
  const out: string[] = [];
  const add = (raw: string | undefined, min: number, cap: number): void => {
    if (!raw) return;
    const t = raw.replace(/\s+/g, " ").trim();
    if (t.length >= min && t.length <= cap) out.push(t);
  };

  add(card.essence, 20, 200); // the headline insight, strongest pull-quote
  for (const sentence of splitSentences(card.body)) add(sentence, 40, 180);
  add(card.whyItMatters, 30, 200); // the "so what"

  const seen = new Set<string>();
  const unique: string[] = [];
  for (const line of out) {
    const key = line.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(line);
    if (unique.length >= max) break;
  }
  return unique;
}

/** Split prose into sentences. Breaks after . ! ? when the next char starts a new
 * sentence (capital/quote/digit), which avoids splitting most mid-sentence periods. */
function splitSentences(text: string | undefined): string[] {
  if (!text) return [];
  return text
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?])\s+(?=["'(]?[A-Z0-9])/)
    .map((s) => s.trim())
    .filter(Boolean);
}
